import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ShortUrl } from "@air/api";
import { CustomFieldTypeName } from "@air/api/types";
import { Button } from "@air/primitive-button";
import { Modal, ModalCloseButton, ModalTitle } from "@air/primitive-modal";
import { useShortIdContext } from "@air/provider-short-id";
import { resetSelectedItemsAction } from "@air/redux-selected-items";
import differenceBy from "lodash/differenceBy";
import intersectionBy from "lodash/intersectionBy";
import pluralize from "pluralize";
import { memo, useCallback, useEffect, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useDispatch, useSelector } from "react-redux";
import { BoardCollectionCustomFieldDateInput } from "~/components/BoardCollection/CustomFields/BoardCollectionCustomFieldDateInput";
import { BoardCollectionCustomFieldPlainText } from "~/components/BoardCollection/CustomFields/BoardCollectionCustomFieldPlainText";
import { BoardCollectionCustomFieldsSelect } from "~/components/BoardCollection/CustomFields/BoardCollectionCustomFieldsSelect";
import { valueIfAllAreEqual } from "~/components/BoardCollection/utils";
import { ASSET_DETAILS_INPUT, BOARD_COLLECTION_ASSET_DETAILS_MODAL } from "~/constants/testIDs";
import { updateBoardCollectionClipsAction } from "~/store/boardCollection/actions";
import { boardCollectionFormSelector, boardCollectionSessionIdSelector } from "~/store/boardCollection/selectors";
import { useAirStore } from "~/utils/ReduxUtils";
import { useContentCollectionCustomFields } from "./hooks/useContentCollectionCustomFields";
export const BoardCollectionGalleryAssetDetailsModal = /*#__PURE__*/ memo((param)=>{
    let { clips, onClose } = param;
    const dispatch = useDispatch();
    const store = useAirStore();
    const { shortId } = useShortIdContext();
    const [customFields, setCustomFields] = useState([]);
    const { data: workspaceCustomFields } = useContentCollectionCustomFields();
    const form = useSelector(boardCollectionFormSelector);
    /** Set initial values when opening the modal after custom fields have been added */ useEffect(()=>{
        if ((workspaceCustomFields === null || workspaceCustomFields === void 0 ? void 0 : workspaceCustomFields.data) && form) {
            const formFields = workspaceCustomFields.data.filter((cf)=>form.fields.find((f)=>f.customFieldId === cf.id));
            setCustomFields(formFields.map((cf)=>{
                switch(cf.type.title){
                    case CustomFieldTypeName.singleSelect:
                        {
                            // when editing a selection, if each clip has the same value for a single select, set that as the initial value
                            const singleSelectValues = clips.map((param)=>{
                                let { customFields } = param;
                                var _customFields_find;
                                return customFields === null || customFields === void 0 ? void 0 : (_customFields_find = customFields.find((param)=>{
                                    let { id } = param;
                                    return id === cf.id;
                                })) === null || _customFields_find === void 0 ? void 0 : _customFields_find.value;
                            });
                            const singleSelectValueIfAllAreEqual = valueIfAllAreEqual(singleSelectValues);
                            return {
                                ...cf,
                                value: singleSelectValueIfAllAreEqual || null,
                                values: null
                            };
                        }
                    case CustomFieldTypeName.multiSelect:
                        {
                            // find intersecting custom values on ALL of the clips and set them as the initial values
                            const multiSelectValues = clips.map((param)=>{
                                let { customFields } = param;
                                var _customFields_find;
                                return (customFields === null || customFields === void 0 ? void 0 : (_customFields_find = customFields.find((param)=>{
                                    let { id } = param;
                                    return id === cf.id;
                                })) === null || _customFields_find === void 0 ? void 0 : _customFields_find.values) || [];
                            });
                            const filteredValues = multiSelectValues.filter((val)=>Array.isArray(val));
                            const intersectingValues = intersectionBy(...filteredValues, "id");
                            return {
                                ...cf,
                                values: intersectingValues.length ? intersectingValues : null,
                                value: null
                            };
                        }
                    case CustomFieldTypeName.plainText:
                        {
                            // if the plain text value is the same across all clips, set that as the initial value
                            const plainTextValues = clips.map((param)=>{
                                let { customFields } = param;
                                var _customFields_find;
                                return customFields === null || customFields === void 0 ? void 0 : (_customFields_find = customFields.find((param)=>{
                                    let { id } = param;
                                    return id === cf.id;
                                })) === null || _customFields_find === void 0 ? void 0 : _customFields_find.plainTextValue;
                            });
                            const plainTextValueIfAllAreEqual = valueIfAllAreEqual(plainTextValues);
                            return {
                                ...cf,
                                values: null,
                                value: null,
                                plainTextValue: plainTextValueIfAllAreEqual
                            };
                        }
                    case CustomFieldTypeName.date:
                        {
                            // if the date value is the same across all clips, set that as the initial value
                            const dates = clips.map((param)=>{
                                let { customFields } = param;
                                var _customFields_find;
                                return customFields === null || customFields === void 0 ? void 0 : (_customFields_find = customFields.find((param)=>{
                                    let { id } = param;
                                    return id === cf.id;
                                })) === null || _customFields_find === void 0 ? void 0 : _customFields_find.dateValue;
                            });
                            const dateIfAllDatesAreEqual = valueIfAllAreEqual(dates);
                            return {
                                ...cf,
                                values: null,
                                value: null,
                                dateValue: dateIfAllDatesAreEqual
                            };
                        }
                    default:
                        {
                            return {
                                ...cf,
                                value: null,
                                values: null
                            };
                        }
                }
            }));
        }
    }, [
        clips,
        form,
        workspaceCustomFields
    ]);
    const saveChanges = useCallback(async ()=>{
        const sessionId = boardCollectionSessionIdSelector(store.getState());
        if (!sessionId) {
            return;
        }
        /** Compile all the custom fields set for the asset, to pass to the API */ const apiCustomFields = customFields.map((cf)=>{
            switch(cf.type.title){
                case CustomFieldTypeName.singleSelect:
                    {
                        var _cf_value;
                        return {
                            customFieldId: cf.id,
                            valueId: (_cf_value = cf.value) === null || _cf_value === void 0 ? void 0 : _cf_value.id
                        };
                    }
                case CustomFieldTypeName.multiSelect:
                    {
                        const filteredValues = clips.map((param)=>{
                            let { customFields } = param;
                            var _customFields_find;
                            return (customFields === null || customFields === void 0 ? void 0 : (_customFields_find = customFields.find((param)=>{
                                let { id } = param;
                                return id === cf.id;
                            })) === null || _customFields_find === void 0 ? void 0 : _customFields_find.values) || [];
                        }).filter((val)=>Array.isArray(val));
                        const intersectingValues = intersectionBy(...filteredValues, "id");
                        const valuesToAdd = differenceBy(cf.values || [], intersectingValues, "id");
                        const valueToRemove = differenceBy(intersectingValues, cf.values || [], "id");
                        return {
                            customFieldId: cf.id,
                            valueIdsToAdd: valuesToAdd.map((param)=>{
                                let { id } = param;
                                return id;
                            }),
                            valueIdsToRemove: valueToRemove.map((param)=>{
                                let { id } = param;
                                return id;
                            })
                        };
                    }
                case CustomFieldTypeName.plainText:
                    {
                        return {
                            customFieldId: cf.id,
                            plainTextValue: cf.plainTextValue
                        };
                    }
                case CustomFieldTypeName.date:
                    {
                        return {
                            customFieldId: cf.id,
                            dateValue: cf.dateValue
                        };
                    }
            }
        });
        ShortUrl.batchUpdateFormAssets(shortId, form.id, {
            formSessionId: sessionId,
            update: {
                customFields: apiCustomFields
            },
            assetIds: clips.map((param)=>{
                let { assetId } = param;
                return assetId;
            })
        });
        dispatch(updateBoardCollectionClipsAction({
            clips: clips.map((clip)=>({
                    id: clip.id,
                    customFields: customFields.map((cf)=>{
                        if (cf.type.title === CustomFieldTypeName.singleSelect) {
                            // when editing a selection, if each clip has the same value for a single select, set that as the initial value
                            if (cf.value) {
                                // if they've added a new value, update it
                                return {
                                    ...cf,
                                    assetId: clip.assetId
                                };
                            } else {
                                var _clip_customFields_find, _clip_customFields;
                                const clipValues = clips.map((param)=>{
                                    let { customFields } = param;
                                    var _customFields_find;
                                    return customFields === null || customFields === void 0 ? void 0 : (_customFields_find = customFields.find((param)=>{
                                        let { id } = param;
                                        return id === cf.id;
                                    })) === null || _customFields_find === void 0 ? void 0 : _customFields_find.value;
                                });
                                const allClipValuesAreEqual = clipValues.every((val, i)=>i === 0 ? true : val === clipValues[i - 1]);
                                const currentClipCFValue = (_clip_customFields = clip.customFields) === null || _clip_customFields === void 0 ? void 0 : (_clip_customFields_find = _clip_customFields.find((param)=>{
                                    let { id } = param;
                                    return id === cf.id;
                                })) === null || _clip_customFields_find === void 0 ? void 0 : _clip_customFields_find.value;
                                return {
                                    ...cf,
                                    assetId: clip.assetId,
                                    value: allClipValuesAreEqual && clipValues[0] ? null : currentClipCFValue !== null && currentClipCFValue !== void 0 ? currentClipCFValue : null
                                };
                            }
                        } else {
                            return {
                                ...cf,
                                assetId: clip.assetId
                            };
                        }
                    })
                }))
        }));
        dispatch(resetSelectedItemsAction());
        onClose();
    }, [
        clips,
        customFields,
        dispatch,
        form,
        onClose,
        shortId,
        store
    ]);
    useHotkeys("enter", saveChanges, {}, [
        saveChanges
    ]);
    return /*#__PURE__*/ _jsxs(Modal, {
        isOpen: true,
        onDismiss: onClose,
        "data-testid": BOARD_COLLECTION_ASSET_DETAILS_MODAL,
        children: [
            /*#__PURE__*/ _jsxs("header", {
                className: "mb-6 flex items-center justify-between gap-4",
                children: [
                    /*#__PURE__*/ _jsxs(ModalTitle, {
                        children: [
                            "Edit ",
                            pluralize("items", clips.length, true)
                        ]
                    }),
                    /*#__PURE__*/ _jsx(ModalCloseButton, {
                        className: "shrink-0",
                        onClick: onClose
                    })
                ]
            }),
            customFields.map((customField, i)=>{
                var _form_fields_find;
                const requiredStatus = form === null || form === void 0 ? void 0 : (_form_fields_find = form.fields.find((param)=>{
                    let { customFieldId } = param;
                    return customFieldId === customField.id;
                })) === null || _form_fields_find === void 0 ? void 0 : _form_fields_find.required;
                return /*#__PURE__*/ _jsx("div", {
                    "data-testid": "".concat(ASSET_DETAILS_INPUT, "-").concat(customField.type.title, "-").concat(customField.name),
                    style: {
                        marginTop: i > 0 ? 16 : 0
                    },
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "flex w-full flex-col items-start justify-start",
                        children: customField.type.title === CustomFieldTypeName.singleSelect || customField.type.title === CustomFieldTypeName.multiSelect ? /*#__PURE__*/ _jsx(BoardCollectionCustomFieldsSelect, {
                            clips: clips,
                            required: requiredStatus,
                            field: customField,
                            onSelectionChange: (param)=>{
                                let { values } = param;
                                setCustomFields(customFields.map((cf)=>{
                                    if (cf.id === customField.id) {
                                        if (cf.type.title === CustomFieldTypeName.singleSelect) {
                                            return {
                                                ...customField,
                                                value: values ? values[0] : null
                                            };
                                        } else {
                                            return {
                                                ...customField,
                                                values
                                            };
                                        }
                                    } else {
                                        return cf;
                                    }
                                }));
                            }
                        }) : customField.type.title === CustomFieldTypeName.plainText ? /*#__PURE__*/ _jsx(BoardCollectionCustomFieldPlainText, {
                            required: requiredStatus,
                            field: customField,
                            onTextChange: (value)=>{
                                setCustomFields(customFields.map((cf)=>{
                                    if (cf.id === customField.id) {
                                        return {
                                            ...customField,
                                            plainTextValue: value
                                        };
                                    } else {
                                        return cf;
                                    }
                                }));
                            }
                        }) : /*#__PURE__*/ _jsx(BoardCollectionCustomFieldDateInput, {
                            required: requiredStatus,
                            field: customField,
                            onDateSelection: (date)=>{
                                setCustomFields(customFields.map((cf)=>{
                                    if (cf.id === customField.id) {
                                        return {
                                            ...customField,
                                            dateValue: date
                                        };
                                    } else {
                                        return cf;
                                    }
                                }));
                            }
                        })
                    })
                }, customField.id);
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-4 flex justify-end gap-2",
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        appearance: "ghost",
                        color: "grey",
                        size: "large",
                        onClick: onClose,
                        children: "Cancel"
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        size: "large",
                        onClick: saveChanges,
                        children: "Save"
                    })
                ]
            })
        ]
    });
});
BoardCollectionGalleryAssetDetailsModal.displayName = "BoardCollectionGalleryAssetDetailsModal";
